import has from 'lodash/has';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import trim from 'lodash/trim';
import dayjs from '@/localPlugins/dayjs';
import { ADVANCED_CONVERSATION_QUERY_FILTERS } from '@/constants';

export const sanitizeAdvancedQueryParams = (queryParams = {}) => {
  const cleanQueryParams = {};
  for (const validKey of Object.keys(ADVANCED_CONVERSATION_QUERY_FILTERS)) {
    if (has(queryParams, validKey) && queryParams[validKey] !== '' && queryParams[validKey] !== null) {
      cleanQueryParams[validKey] = queryParams[validKey];
    }
  }
  return cleanQueryParams;
};

export const PRESET_FILTER_KEYS = [
  'queued',
  'unresponded',
  'ai_served',
  'agent_served',
  'active',
  'unlocked',
  'typed',
  'i_am_primary',
];

export const SEARCH_TYPES = ['all', 'email', 'message_content', 'name', 'phone'];
const TYPED_SEARCH_PREFIXES = ['all', 'email', 'name', 'phone'];

export const DEFAULT_PRESET_VALUES = PRESET_FILTER_KEYS.reduce((acc, key) => ({
  ...acc,
  [key]: false,
}), {});

export const DEFAULT_SEARCH = {
  searchType: 'all',
  searchValue: '',
};

export const DEFAULT_FILTER = {
  dateRange: { start_date: null, end_date: null },
  ...DEFAULT_PRESET_VALUES,
  ...DEFAULT_SEARCH,
  tag_name: null,
  category_name: null,
  segment_names: [],
  agent_id: null,
  team_id: null,
  agent_team_id: null,
  last_feedback_score: null,
};

const toArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const findById = (items, id) => items.find((item) => String(item.id) === String(id));

const parseIdParam = (value) => (
  value !== null && value !== undefined && value !== '' ? value : null
);

const parseNameParam = (value) => {
  if (isNil(value)) {
    return null;
  }
  const normalized = trim(String(value));
  return normalized || null;
};

const coerceId = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : num;
};

const coerceNameList = (values = []) => toArray(values)
  .map(parseNameParam)
  .filter(Boolean)
  .sort();

export const coerceFilterState = (filterState = {}) => ({
  dateRange: {
    start_date: filterState.dateRange?.start_date || null,
    end_date: filterState.dateRange?.end_date || null,
  },
  ...PRESET_FILTER_KEYS.reduce((acc, key) => ({
    ...acc,
    [key]: Boolean(filterState[key]),
  }), {}),
  searchType: filterState.searchType || DEFAULT_SEARCH.searchType,
  searchValue: (filterState.searchValue || '').trim(),
  tag_name: parseNameParam(filterState.tag_name),
  category_name: parseNameParam(filterState.category_name),
  segment_names: coerceNameList(filterState.segment_names),
  agent_id: coerceId(filterState.agent_id),
  team_id: coerceId(filterState.team_id),
  agent_team_id: coerceId(filterState.agent_team_id),
  last_feedback_score: toFeedbackScore(filterState.last_feedback_score),
});

const ADVANCED_ARRAY_QUERY_KEYS = [
  'tag_names',
  'category_names',
  'customer_segments',
  'teams',
  'agent_teams',
  'agent',
  'date_range',
];

export const adjustAdvancedUrlArrays = (query = {}) => {
  ADVANCED_ARRAY_QUERY_KEYS.forEach((key) => {
    if (query[key] !== undefined && query[key] !== null && !Array.isArray(query[key])) {
      query[key] = [query[key]];
    }
  });
};

const formatTypedSearchQuery = (prefix, value) => {
  const formattedValue = /\s/.test(value) ? `"${value}"` : value;
  return `${prefix}:${formattedValue}`;
};

const buildAppliedSearchFields = ({ searchType, searchValue }) => {
  const value = (searchValue || '').trim();
  if (!value) {
    return { query: '', message_content: '' };
  }

  if (searchType === 'message_content') {
    return { query: '', message_content: value };
  }

  if (TYPED_SEARCH_PREFIXES.includes(searchType)) {
    return {
      query: formatTypedSearchQuery(searchType, value),
      message_content: '',
    };
  }

  return { query: value, message_content: '' };
};

const parseBooleanQueryParam = (value) => value === 'true' || value === true;

const toFeedbackScore = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const resolveSearchFromUrlQuery = (routeQuery = {}) => {
  if (routeQuery.message_content) {
    return { searchType: 'message_content', searchValue: routeQuery.message_content };
  }
  if (routeQuery.name) {
    return { searchType: 'name', searchValue: routeQuery.name };
  }
  if (routeQuery.email) {
    return { searchType: 'email', searchValue: routeQuery.email };
  }
  if (routeQuery.phone) {
    return { searchType: 'phone', searchValue: routeQuery.phone };
  }
  if (routeQuery.search_type || routeQuery.search) {
    return {
      searchType: routeQuery.search_type || 'all',
      searchValue: routeQuery.search || '',
    };
  }
  // Legacy: bare `query` URL param is an "all" text search value.
  if (routeQuery.query) {
    return { searchType: 'all', searchValue: routeQuery.query };
  }
  return { searchType: 'all', searchValue: '' };
};

const writeSearchToUrlQuery = (rawQuery, { searchType, searchValue }) => {
  const value = (searchValue || '').trim();
  if (!value) {
    return;
  }

  if (searchType === 'message_content') {
    rawQuery.message_content = value;
    return;
  }

  if (searchType === 'name' || searchType === 'email' || searchType === 'phone') {
    rawQuery[searchType] = value;
    return;
  }

  rawQuery.search_type = searchType || 'all';
  rawQuery.search = value;
};

export const ADVANCED_QUERY_PARAM_KEYS = Object.values(ADVANCED_CONVERSATION_QUERY_FILTERS);

export const stripAdvancedQueryParams = (query = {}) => {
  const result = { ...query };
  ADVANCED_QUERY_PARAM_KEYS.forEach((key) => {
    delete result[key];
  });
  return result;
};

export const parseFilterFromUrlQuery = (routeQuery = {}) => {
  const presets = PRESET_FILTER_KEYS.reduce((acc, key) => ({
    ...acc,
    [key]: routeQuery[key] !== undefined
      ? parseBooleanQueryParam(routeQuery[key])
      : false,
  }), {});

  const { searchType, searchValue } = resolveSearchFromUrlQuery(routeQuery);
  const date_range = toArray(routeQuery.date_range);

  return coerceFilterState({
    ...presets,
    searchType,
    searchValue,
    dateRange: {
      start_date: date_range[0] ? dayjs.unix(Number(date_range[0])).toISOString() : null,
      end_date: date_range[1] ? dayjs.unix(Number(date_range[1])).toISOString() : null,
    },
    tag_name: parseNameParam(toArray(routeQuery.tag_names)[0]),
    category_name: parseNameParam(toArray(routeQuery.category_names)[0]),
    segment_names: coerceNameList(routeQuery.customer_segments),
    agent_id: parseIdParam(toArray(routeQuery.agent)[0]),
    team_id: parseIdParam(toArray(routeQuery.teams)[0]),
    agent_team_id: parseIdParam(toArray(routeQuery.agent_teams)[0]),
    last_feedback_score: toFeedbackScore(routeQuery.last_feedback_score),
  });
};

export const buildUrlQueryFromFilterState = (filterState = {}) => {
  const {
    dateRange = { start_date: null, end_date: null },
    tag_name = null,
    category_name = null,
    segment_names = [],
    agent_id = null,
    team_id = null,
    agent_team_id = null,
    last_feedback_score = null,
  } = coerceFilterState(filterState);

  const rawQuery = {};

  PRESET_FILTER_KEYS.forEach((key) => {
    if (filterState[key]) {
      rawQuery[key] = 'true';
    }
  });

  writeSearchToUrlQuery(rawQuery, {
    searchType: filterState.searchType,
    searchValue: filterState.searchValue,
  });

  if (tag_name !== null) {
    rawQuery.tag_names = [tag_name];
  }

  if (category_name !== null) {
    rawQuery.category_names = [category_name];
  }

  if (segment_names.length) {
    rawQuery.customer_segments = segment_names;
  }

  if (team_id !== null) {
    rawQuery.teams = [String(team_id)];
  }

  if (agent_team_id !== null) {
    rawQuery.agent_teams = [String(agent_team_id)];
  }

  if (agent_id !== null) {
    rawQuery.agent = [String(agent_id)];
  }

  if (dateRange.start_date || dateRange.end_date) {
    rawQuery.date_range = [
      dateRange.start_date ? dayjs(dateRange.start_date).unix() : '',
      dateRange.end_date ? dayjs(dateRange.end_date).unix() : '',
    ];
  }

  if (last_feedback_score !== null && last_feedback_score !== undefined) {
    rawQuery.last_feedback_score = String(last_feedback_score);
  }

  return sanitizeAdvancedQueryParams(rawQuery);
};

export const buildServerPayload = (filterState = {}) => {
  const normalized = coerceFilterState(filterState);
  const searchFields = buildAppliedSearchFields(normalized);
  const query = [
    searchFields.query,
    normalized.tag_name && formatTypedSearchQuery('tag', normalized.tag_name),
    normalized.category_name && formatTypedSearchQuery('category', normalized.category_name),
  ].filter(Boolean).join(' ');

  return {
    ...PRESET_FILTER_KEYS.reduce((acc, key) => ({
      ...acc,
      [key]: Boolean(normalized[key]),
    }), {}),
    ...searchFields,
    ...(query && { query }),
    dateRange: {
      start_date: normalized.dateRange.start_date,
      end_date: normalized.dateRange.end_date,
    },
    agent_id: normalized.agent_id,
    team_id: normalized.team_id,
    agent_team_id: normalized.agent_team_id,
    last_feedback_score: normalized.last_feedback_score,
    segments: normalized.segment_names,
    teams: normalized.team_id !== null ? [String(normalized.team_id)] : [],
    agent_teams: normalized.agent_team_id !== null ? [String(normalized.agent_team_id)] : [],
  };
};

// Backward-compatible alias used by the component.
export const buildAppliedFilterPayload = buildServerPayload;

const canonicalizeServerPayload = (payload = {}) => ({
  ...PRESET_FILTER_KEYS.reduce((acc, key) => ({
    ...acc,
    [key]: Boolean(payload[key]),
  }), {}),
  query: (payload.query || '').trim(),
  message_content: (payload.message_content || '').trim(),
  dateRange: {
    start_date: payload.dateRange?.start_date || null,
    end_date: payload.dateRange?.end_date || null,
  },
  agent_id: coerceId(payload.agent_id),
  team_id: coerceId(payload.team_id),
  agent_team_id: coerceId(payload.agent_team_id),
  last_feedback_score: toFeedbackScore(payload.last_feedback_score),
  segments: toArray(payload.segments).map(String).sort(),
  teams: toArray(payload.teams).map(String).sort(),
  agent_teams: toArray(payload.agent_teams).map(String).sort(),
});

export const areAppliedFiltersEqual = (left, right) => (
  isEqual(canonicalizeServerPayload(left), canonicalizeServerPayload(right))
);

export const isAppliedFilterEmpty = (appliedFilter = {}) => {
  if (!appliedFilter || Object.keys(appliedFilter).length === 0) {
    return true;
  }

  return areAppliedFiltersEqual(appliedFilter, buildServerPayload(DEFAULT_FILTER));
};

export const getActivePresetKeys = (filter, includePrimaryAgent = true) => (
  PRESET_FILTER_KEYS.filter((key) => {
    if (key === 'i_am_primary' && !includePrimaryAgent) return false;
    return filter[key];
  })
);

export const hasSearchValue = (filter) => Boolean((filter.searchValue || '').trim());

export const areFiltersEqual = (left, right) => (
  isEqual(coerceFilterState(left), coerceFilterState(right))
);

export const isFilterFilled = (filter, includePrimaryAgent = true) => (
  getActivePresetKeys(filter, includePrimaryAgent).length > 0
  || hasSearchValue(filter)
  || Boolean(filter.tag_name)
  || Boolean(filter.category_name)
  || Boolean(filter.agent_id)
  || Boolean(filter.team_id)
  || Boolean(filter.agent_team_id)
  || filter.last_feedback_score !== null && filter.last_feedback_score !== undefined
  || (filter.segment_names || []).length > 0
  || Boolean(filter.dateRange?.start_date || filter.dateRange?.end_date)
);

export const hasAdvancedFilterQueryParams = (query = {}) => {
  if (PRESET_FILTER_KEYS.some((key) => parseBooleanQueryParam(query[key]))) {
    return true;
  }

  if (query.query || query.search || query.search_type
    || query.message_content || query.name || query.email || query.phone) {
    return true;
  }

  const arrayParams = [
    ...toArray(query.tag_names),
    ...toArray(query.category_names),
    ...toArray(query.customer_segments),
    ...toArray(query.teams),
    ...toArray(query.agent_teams),
  ];
  if (arrayParams.length) {
    return true;
  }

  if (query.agent) {
    return true;
  }

  if (query.date_range) {
    return true;
  }

  return query.last_feedback_score !== undefined && query.last_feedback_score !== '';
};

export const findFilterItemById = findById;
